<template>
  <div>
    <ts-loading-banner :loading="loading">
      <div class="ts-space-y-3">
        <div class="row">
          <div class="col-md-12">
            <label class="required">{{
              $t('monthly-semester-mapping.main_program')
            }}</label>
            <a-select
              v-model="model.acp_id"
              :placeholder="$t('monthly-semester-mapping.select_main_program')"
              show-search
              :filter-option="filterOption"
              :loading="loading"
              :disabled="loading"
              style="width: 100%"
              :options="mainProgramOptions"
              @change="getSubProgramOptions"
            ></a-select>
            <div v-if="errors.has('acp_id')">
              <span class="text-danger">{{ errors.first('acp_id') }}</span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="required">{{
              $t('monthly-semester-mapping.sub_program')
            }}</label>
            <a-select
              v-model="model.acps_id"
              :placeholder="$t('monthly-semester-mapping.select_sub_program')"
              show-search
              :filter-option="filterOption"
              :loading="loading"
              :disabled="loading"
              style="width: 100%"
              :options="subProgramOptions"
              @change="subProgramChange"
            ></a-select>
            <div v-if="errors.has('acps_id')">
              <span class="text-danger">{{ errors.first('acps_id') }}</span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="required">{{
              $t('monthly-semester-mapping.semester_order')
            }}</label>
            <a-select
              v-model="model.semester_order"
              :placeholder="
                $t('monthly-semester-mapping.select_semester_order')
              "
              show-search
              :filter-option="filterOption"
              :loading="loading"
              :disabled="loading"
              style="width: 100%"
              :options="semesterOptions"
            ></a-select>
            <div v-if="errors.has('semester_order')">
              <span class="text-danger">{{
                errors.first('semester_order')
              }}</span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="required">{{
              $t('monthly-semester-mapping.month')
            }}</label>
            <a-select
              v-model="model.months"
              :placeholder="$t('monthly-semester-mapping.select_month')"
              mode="multiple"
              show-search
              :filter-option="filterOption"
              :loading="loading"
              :disabled="loading"
              style="width: 100%"
              :options="monthOptions"
            ></a-select>
            <div v-if="errors.has('months')">
              <span class="text-danger">{{ errors.first('months') }}</span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label>{{ $t('monthly-semester-mapping.remark') }}</label>
            <a-textarea
              v-model="model.remark"
              :placeholder="$t('monthly-semester-mapping.remark')"
              :auto-size="{ minRows: 3, maxRows: 5 }"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2">
            <a-button @click="closeForm" type="default">
              {{ $t('cancel') }}
            </a-button>
            <a-button v-if="!editData" type="dashed" @click="handleSaveAddNew">
              {{ $t('save_add_new') }}
            </a-button>
            <a-button v-if="!editData" type="primary" @click="handleSave">
              {{ $t('save') }}
            </a-button>
            <a-button v-else type="primary" @click="handleUpdate">
              {{ $t('update') }}
            </a-button>
          </div>
        </div>
      </div>
    </ts-loading-banner>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import notificationAlert from '@/utils/NotificationAlert'

export default {
  name: 'MonthlySemesterMappingForm',
  props: {
    editData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      errors: new Errors(),
      mainProgramOptions: [],
      subProgramOptions: [],
      semesterOptions: [],
      model: {
        id: undefined,
        acp_id: undefined,
        acps_id: undefined,
        semester_order: undefined,
        months: [],
        remark: null
      }
    }
  },
  async mounted() {
    await this.getMainProgramOptions()
    await this.setData()
  },
  computed: {
    monthOptions() {
      return [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ].map((month, index) => {
        return {
          value: index + 1,
          label: month
        }
      })
    }
  },
  methods: {
    ...mapActions('monthlySemesterMapping', [
      'formViewModel',
      'onSave',
      'onUpdate'
    ]),
    async getMainProgramOptions() {
      this.loading = true
      return this.formViewModel({
        fnName: 'main_program'
      })
        .then(response => {
          if (response.status === 200) {
            this.mainProgramOptions = response.data.main_program.map(item => {
              return {
                value: item.acp_id,
                label: item.acp_name_native
              }
            })
          } else {
            this.$message.error(response.data.message)
          }
        })
        .catch(error => {
          notificationAlert(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async getSubProgramOptions() {
      this.loading = true
      this.model.acps_id = undefined
      this.model.semester_order = undefined
      this.semesterOptions = []
      return this.formViewModel({
        fnName: 'sub_program',
        acp_id: this.model.acp_id
      })
        .then(response => {
          if (response.status === 200) {
            this.subProgramOptions = response.data.sub_program.map(item => {
              return {
                value: item.acps_id,
                label: item.acps_name_native,
                number_of_term_per_ac_year: item.number_of_term_per_ac_year
              }
            })
          } else {
            this.$message.error(response.data.message)
          }
        })
        .catch(error => {
          notificationAlert(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    subProgramChange(value, { data }) {
      this.model.semester_order = undefined
      if (data.props.number_of_term_per_ac_year) {
        this.semesterOptions = []
        for (let i = 1; i <= data.props.number_of_term_per_ac_year; i++) {
          this.semesterOptions.push({
            value: i,
            label: this.$t('monthly-semester-mapping.semester') + ' ' + i
          })
        }
      } else {
        this.semesterOptions = []
      }
    },
    closeForm() {
      this.$emit('close')
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    },
    handleSaveAddNew() {
      this.loading = true
      this.errors.clear()
      this.onSave(this.model)
        .then(response => {
          notificationAlert(response)
          this.resetInput()
        })
        .catch(error => {
          notificationAlert(error)
          this.errors.record(error.response.data.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSave() {
      this.loading = true
      this.errors.clear()
      this.onSave(this.model)
        .then(response => {
          notificationAlert(response)
          this.resetInput()
          this.closeForm()
        })
        .catch(error => {
          notificationAlert(error)
          this.errors.record(error.response.data.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    resetInput() {
      this.model = {
        id: undefined,
        acp_id: undefined,
        acps_id: undefined,
        semester_order: undefined,
        months: [],
        remark: null
      }
    },
    async setData() {
      if (this.editData) {
        this.model.id = this.editData.id
        this.model.acp_id = this.editData.acp_id
        await this.getSubProgramOptions()
        this.model.acps_id = this.editData.acps_id
        this.model.semester_order = this.editData.semester_order
        this.model.months = this.editData.months
        this.model.remark = this.editData.remark

        this.semesterOptions = []

        const subProgram = this.subProgramOptions.find(
          item => item.value === this.editData.acps_id
        )

        if (subProgram) {
          for (let i = 1; i <= subProgram.number_of_term_per_ac_year; i++) {
            this.semesterOptions.push({
              value: i,
              label: this.$t('monthly-semester-mapping.semester') + ' ' + i
            })
          }
        }
      } else {
        this.resetInput()
      }
    },
    handleUpdate(){
      this.loading = true
      this.errors.clear()
      this.onUpdate(this.model)
        .then(response => {
          notificationAlert(response)
          this.resetInput()
          this.closeForm()
        })
        .catch(error => {
          notificationAlert(error)
          this.errors.record(error.response.data.errors)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
